import {
    Grid,
    Typography,
    TextField,
    Button,
    Paper,
    CircularProgress,
    Snackbar,
    Alert,
    useMediaQuery,
    Divider,
    Autocomplete
} from "@mui/material";
import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import NavigatedComponent from "../NavigatedComponent";
import Header from "../../common/Header";
import Sidenav from "../../common/Sidenav";
import IconSidenav from "../../common/IconSidenav";
import JoditEditor from "jodit-react";
import { apiList, invokeApi } from "../../../services/apiServices";
import { config } from "../../../config/config";
import { useCookies } from "react-cookie";

const AddArticle = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [cookies] = useCookies();
    const editor = useRef(null);
    const location = useLocation();

    const [title, setTitle] = useState(""); // Title is hidden, will default to Category or empty
    const [year, setYear] = useState("");
    const [category, setCategory] = useState("");
    const [content, setContent] = useState("");

    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [showSideNav, setShowSideNav] = useState(false);
    const [existingCategories, setExistingCategories] = useState([]);

    const isMobileScreen = useMediaQuery("(min-width:360px) and (max-width:500px)");

    useEffect(() => {
        if (id) {
            fetchArticleData();
        }
        fetchCategories();
    }, [id]);

    const toggleSideNav = () => {
        setShowSideNav(!showSideNav);
    };

    const fetchCategories = async () => {
        try {
            const response = await invokeApi(config.getMyCollege + apiList.getArticles, {}, cookies);
            if (response?.status === 200 && response?.data?.articles) {
                const categories = [...new Set(response.data.articles.map(article => article.categoryName || article.category || article.title).filter(Boolean))];
                setExistingCategories(categories);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    };

    const fetchArticleData = async () => {
        setFetching(true);
        try {
            const response = await invokeApi(config.getMyCollege + apiList.getArticle, { id }, cookies);
            if (response?.status === 200 && response?.data?.article) {
                const { title, year, content, categoryName, category } = response.data.article;
                // FIX: Title holds the Category Name effectively (due to backend issue). 
                // Content holds the Content.
                // Reset state accordingly.
                setTitle(title || "");
                setYear(year);
                // Priority: categoryName -> category -> title (because we store Category in Title now)
                setCategory(categoryName || category || title || "");
                setContent(content);
            } else {
                setSnackbar({ open: true, message: "Failed to load article details", severity: "error" });
            }
        } catch (error) {
            console.error("Error fetching article:", error);
        } finally {
            setFetching(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // Title defaults to category if missing (since removed from UI)
        const finalTitle = title || category;

        if (!year || !category || !content) {
            setSnackbar({ open: true, message: "Please fill all required fields", severity: "warning" });
            return;
        }

        setLoading(true);
        const api = id ? apiList.updateArticle : apiList.addArticle;
        // FIX: Reverting previous "workaround". 
        // We verified that payload.content -> DB.content.
        // So we must send CONTENT in the content field.
        // We store Category in Title field (as Title is displayed as Category in frontend).

        const payload = {
            title: category,       // Store Category Name in Title
            year,
            categoryName: category, // Also send Category in categoryName field just in case
            content: content,      // Send Content in Content field (CORRECT)
            ...(id && { id })
        };

        try {
            const response = await invokeApi(config.getMyCollege + api, payload, cookies);
            if (response?.status >= 200 && response?.status < 300) {
                setSnackbar({ open: true, message: `Article ${id ? "updated" : "added"} successfully`, severity: "success" });
                setTimeout(() => navigate("/publication-manager/article-list"), 1500);
            } else {
                setSnackbar({ open: true, message: "Operation failed", severity: "error" });
            }
        } catch (error) {
            console.error("Error saving article:", error);
            setSnackbar({ open: true, message: "Something went wrong", severity: "error" });
        } finally {
            setLoading(false);
        }
    };

    if (fetching) return <CircularProgress sx={{ display: "block", margin: "auto", mt: 5 }} />;

    return (
        <Grid sx={{ display: "flex" }}>
            {isMobileScreen ? (
                <IconSidenav />
            ) : showSideNav ? (
                <IconSidenav />
            ) : (
                <Sidenav />
            )}
            <Grid component="main" sx={{ width: "100%", px: 1 }}>
                <Header toggleSideNav={toggleSideNav} />
                <Grid sx={{ py: 1 }}>
                    <NavigatedComponent pathname={location.pathname} />
                    <Grid container justifyContent="center" sx={{ p: 3 }}>
                        <Grid item xs={12} md={10}>
                            <Paper sx={{ p: 4, my: 2 }}>
                                <Typography variant="h5" fontWeight="bold" gutterBottom>
                                    {id ? "Edit Article" : "Add Article"}
                                </Typography>
                                <Divider sx={{ mb: 3 }} />
                                <form onSubmit={handleSubmit}>

                                    <TextField
                                        fullWidth
                                        label="Year"
                                        type="number"
                                        value={year}
                                        onChange={(e) => setYear(e.target.value)}
                                        margin="normal"
                                        required
                                    />
                                    <Autocomplete
                                        freeSolo
                                        options={existingCategories}
                                        value={category}
                                        onChange={(event, newValue) => {
                                            setCategory(newValue);
                                        }}
                                        onInputChange={(event, newInputValue) => {
                                            setCategory(newInputValue);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Category"
                                                margin="normal"
                                                required
                                                fullWidth
                                            />
                                        )}
                                    />

                                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Citation / Content (Note: Content will use "Insert only text" while pasting)</Typography>
                                    <JoditEditor
                                        ref={editor}
                                        value={content}
                                        tabIndex={1}
                                        onBlur={(newContent) => setContent(newContent)}
                                        onChange={() => { }}
                                    />

                                    <Button
                                        type="submit"
                                        variant="contained"
                                        fullWidth
                                        sx={{ mt: 3, bgcolor: "#262272" }}
                                        disabled={loading}
                                    >
                                        {loading ? <CircularProgress size={24} color="inherit" /> : id ? "Update Article" : "Add Article"}
                                    </Button>
                                </form>
                            </Paper>
                        </Grid>
                        <Snackbar
                            open={snackbar.open}
                            autoHideDuration={6000}
                            onClose={() => setSnackbar({ ...snackbar, open: false })}
                        >
                            <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                                {snackbar.message}
                            </Alert>
                        </Snackbar>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default AddArticle;
